import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PickerMap } from '../maps/OsmMap';
import { Bike, Search, Loader2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DeliverySettings = ({ onSaved }) => {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => { api.get('/offsite/config').then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const setPin = useCallback((lat, lng) => set('restaurant_geo', { lat, lng }), []);

  const search = async (e) => {
    e?.preventDefault();
    if (query.trim().length < 3) return;
    setSearching(true); setSuggestions([]);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/geocode/search`, { params: { q: query.trim(), country: 'eu' } });
      setSuggestions(data.results || []);
      if ((data.results || []).length === 1) { const r = data.results[0]; setPin(r.lat, r.lon); set('restaurant_address', r.display_name); setSuggestions([]); }
    } catch (e) { /* */ } finally { setSearching(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/offsite/config', {
        takeaway_enabled: !!cfg.takeaway_enabled,
        delivery_enabled: !!cfg.delivery_enabled,
        restaurant_geo: cfg.restaurant_geo,
        restaurant_address: cfg.restaurant_address || '',
        radius_km: Number(cfg.radius_km) || 5,
        delivery_fee: Number(cfg.delivery_fee) || 0,
        min_order: Number(cfg.min_order) || 0,
        open_time: cfg.open_time || '11:00',
        close_time: cfg.close_time || '23:00',
        slot_minutes: Number(cfg.slot_minutes) || 15,
        lead_minutes: Number(cfg.lead_minutes) || 30,
        country: cfg.country || 'it',
      });
      setCfg(data);
      toast.success(t('offsite.settings.saved'));
      if (onSaved) onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  if (!cfg) return null;
  const pin = cfg.restaurant_geo && cfg.restaurant_geo.lat != null ? cfg.restaurant_geo : null;

  return (
    <Card className="p-5" data-testid="delivery-settings-card">
      <div className="font-semibold mb-1 flex items-center gap-2"><Bike className="h-4 w-4 text-primary" /> {t('offsite.settings.title')}</div>
      <p className="text-sm text-muted-foreground mb-4">{t('offsite.settings.subtitle')}</p>

      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-xl border p-3">
            <Label htmlFor="tw">{t('offsite.settings.enableTakeaway')}</Label>
            <Switch id="tw" checked={!!cfg.takeaway_enabled} onCheckedChange={(v) => set('takeaway_enabled', v)} data-testid="settings-enable-takeaway-switch" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-3">
            <Label htmlFor="dl">{t('offsite.settings.enableDelivery')}</Label>
            <Switch id="dl" checked={!!cfg.delivery_enabled} onCheckedChange={(v) => set('delivery_enabled', v)} data-testid="settings-enable-delivery-switch" />
          </div>
        </div>

        {/* Location */}
        <div>
          <Label>{t('offsite.settings.location')}</Label>
          <p className="text-xs text-muted-foreground mb-1.5">{t('offsite.settings.locationHint')}</p>
          <form onSubmit={search} className="flex gap-2 mb-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('offsite.addressPlaceholder')} data-testid="settings-location-search-input" />
            <Button type="submit" variant="secondary" disabled={searching} className="shrink-0">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
          </form>
          {suggestions.length > 0 && (
            <div className="mb-2 rounded-xl border bg-card divide-y">
              {suggestions.map((r, i) => (
                <button key={i} type="button" onClick={() => { setPin(r.lat, r.lon); set('restaurant_address', r.display_name); setSuggestions([]); setQuery(r.display_name); }}
                  className="flex w-full items-start gap-2 p-2.5 text-left text-sm hover:bg-muted">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" /><span className="line-clamp-2">{r.display_name}</span>
                </button>
              ))}
            </div>
          )}
          <PickerMap center={pin || { lat: 45.4642, lng: 9.19 }} pin={pin} radiusM={(Number(cfg.radius_km) || 5) * 1000}
            onChange={setPin} testId="settings-restaurant-map"
            className="h-[260px] rounded-[var(--radius)] overflow-hidden border shadow-sm-token" />
        </div>

        {/* Radius */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>{t('offsite.settings.radius')}</Label>
            <span className="tabular font-display font-bold text-primary">{Number(cfg.radius_km) || 5} km</span>
          </div>
          <Slider min={1} max={30} step={1} value={[Number(cfg.radius_km) || 5]} onValueChange={(v) => set('radius_km', v[0])} data-testid="settings-delivery-radius-slider" />
        </div>

        {/* Numbers */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t('offsite.settings.fee')} (€)</Label><Input type="number" step="0.5" value={cfg.delivery_fee ?? 0} onChange={(e) => set('delivery_fee', e.target.value)} className="mt-1.5" data-testid="settings-delivery-fee-input" /></div>
          <div><Label>{t('offsite.settings.minOrder')} (€)</Label><Input type="number" step="1" value={cfg.min_order ?? 0} onChange={(e) => set('min_order', e.target.value)} className="mt-1.5" data-testid="settings-min-order-input" /></div>
          <div><Label>{t('offsite.settings.openTime')}</Label><Input type="time" value={cfg.open_time || '11:00'} onChange={(e) => set('open_time', e.target.value)} className="mt-1.5" data-testid="settings-open-time-input" /></div>
          <div><Label>{t('offsite.settings.closeTime')}</Label><Input type="time" value={cfg.close_time || '23:00'} onChange={(e) => set('close_time', e.target.value)} className="mt-1.5" data-testid="settings-close-time-input" /></div>
          <div>
            <Label>{t('offsite.settings.slotMinutes')}</Label>
            <Select value={String(cfg.slot_minutes || 15)} onValueChange={(v) => set('slot_minutes', Number(v))}>
              <SelectTrigger className="mt-1.5" data-testid="settings-slot-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[15, 20, 30, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t('offsite.settings.leadMinutes')}</Label><Input type="number" step="5" value={cfg.lead_minutes ?? 30} onChange={(e) => set('lead_minutes', e.target.value)} className="mt-1.5" data-testid="settings-lead-minutes-input" /></div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="settings-save-button">{saving ? '...' : t('offsite.settings.save')}</Button>
      </div>
    </Card>
  );
};
